import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AccordionWrapper from 'App/components/AccordionWrapper/AccordionWrapper';
import { defaultVisibilityState } from 'App/components/PatientProfile/PatientProfile.utils.js';
import AddPatientActivityModal from './AddPatientActivityModal';
import EditActivityDueDateModal from './components/EditActivityDueDateModal';
import { Button } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import ActivityNotesModal from './components/ActivityNotesModal';
import './PatientActivities.scss';
import PatientActivitiesContent from './PatientActivitiesContent';
import { queryClient } from 'Shared/queryClient';

export const ActivityTypes = {
    PendingActivities: 0,
    PastActivities: 1,
    DeletedActivities: 2,
};

const ActivityTitle = {
    [ActivityTypes.PendingActivities]: 'Pending Activities',
    [ActivityTypes.PastActivities]: 'Past Activities',
    [ActivityTypes.DeletedActivities]: 'Deleted Activities',
};

const ActivityVisibility = {
    [ActivityTypes.PendingActivities]: 'pendingActivities',
    [ActivityTypes.PastActivities]: 'pastActivities',
    [ActivityTypes.DeletedActivities]: 'deletedActivities',
};

const propTypes = {
    id: PropTypes.string.isRequired,
    activityType: PropTypes.number.isRequired,
    divisionId: PropTypes.string,
    therapy: PropTypes.arrayOf(PropTypes.shape({})),
};

const defaultProps = {
    divisionId: '',
    therapy: [],
};

const PatientActivities = ({ id, activityType, divisionId, therapy }) => {
    const [showAddPatientActivityModal, setShowAddPatientActivityModal] = useState(false);
    const [showDueDateModal, setShowDueDateModal] = useState(false);
    const [currentActivity, setCurrentActivity] = useState(null);
    const [showActivityStatusModal, setShowActivityStatusModal] = useState(false);

    const triggerRefetch = () => {
        queryClient.invalidateQueries(['patient', id, 'activities']);
        queryClient.invalidateQueries(['patient', id, 'referrals']);
    };

    const openActivityModal = (activity) => {
        setCurrentActivity(activity);
        setShowDueDateModal(true);
    };

    const hideActivityModal = (refetch) => {
        if (refetch) {
            triggerRefetch();
        }
        setShowDueDateModal(false);
        setCurrentActivity(null);
    };

    const openActivityNoteModal = (activity) => {
        setCurrentActivity(activity);
        setShowActivityStatusModal(true);
    };

    const hideActivityNoteModal = (refetch) => {
        if (refetch) {
            triggerRefetch();
        }
        setShowActivityStatusModal(false);
        setCurrentActivity(null);
    };

    return (
        <>
            <AccordionWrapper
                title={ActivityTitle[activityType]}
                stateKey={`patientProfile_${ActivityVisibility[activityType]}`}
                defaultIsVisible={defaultVisibilityState[ActivityVisibility[activityType]]}
                ActionElement={
                    activityType === ActivityTypes.PendingActivities ? (
                        <div className="add-create-action">
                            <Button
                                variant="link"
                                size="sm"
                                aria-label="Add Activity"
                                title="Add Activity"
                                onClick={() => setShowAddPatientActivityModal(true)}
                            >
                                <FaPlus /> Add Activity
                            </Button>
                        </div>
                    ) : null
                }
            >
                <PatientActivitiesContent
                    id={id}
                    activityType={activityType}
                    divisionId={divisionId}
                    openActivityModal={openActivityModal}
                    openActivityNoteModal={openActivityNoteModal}
                />
            </AccordionWrapper>
            {showAddPatientActivityModal && (
                <div>
                    <AddPatientActivityModal
                        patientId={id}
                        onHide={() => {
                            hideActivityModal(true);
                            setShowAddPatientActivityModal(false);
                        }}
                        patientTherapies={therapy}
                    />
                </div>
            )}
            {showActivityStatusModal && (
                <div>
                    <ActivityNotesModal
                        activity={currentActivity}
                        patientId={id}
                        show={showActivityStatusModal}
                        onHide={hideActivityNoteModal}
                    />
                </div>
            )}
            {showDueDateModal && (
                <EditActivityDueDateModal
                    show={showDueDateModal}
                    onHide={hideActivityModal}
                    activity={currentActivity}
                />
            )}
        </>
    );
};

PatientActivities.propTypes = propTypes;
PatientActivities.defaultProps = defaultProps;

export default PatientActivities;
